import { Reducer } from 'redux';

import { ActionType } from '../../../../store/actionTypes';
import { CollectionState, FetchAction, FetchActionTypes } from './simple';

export interface PaginatedCollectionState {
  [key: string]: {
    [page: number]: CollectionState;
    total: number;
  };
}

function getKey(query: string) {
  return `searchPhrase__${query}`;
}

export function createPaginatedReducer<
  ST extends ActionType,
  SU extends ActionType,
  FA extends ActionType,
>(
  actionTypes: FetchActionTypes<ST, SU, FA>,
): Reducer<PaginatedCollectionState, FetchAction<ST, SU, FA>> {
  return function (
    state: PaginatedCollectionState = {},
    action: FetchAction<ST, SU, FA>,
  ): PaginatedCollectionState {
    let query: string | undefined;
    let page: number | undefined;

    if (action.payload) {
      query = action.payload.query;
      page = action.payload.page;
    }

    if (query !== undefined && page) {
      const key = getKey(query);
      switch (action.type) {
        case actionTypes.started:
          return {
            ...state,
            [key]: {
              ...state[key],
              [page]: {
                ...(state[key] ? state[key][page] : { items: [] }),
                isFetching: true,
              },
            },
          };

        case actionTypes.succeeded:
          return {
            ...state,
            [key]: {
              ...state[key],
              [page]: {
                ...state[key][page],
                isFetching: false,
                items: action.payload.result,
              },
              total: action.payload.total,
            },
          };

        case actionTypes.failed:
          return {
            ...state,
            [key]: {
              ...state[key],
              [page]: {
                ...state[key][page],
                isFetching: false,
                error: action.payload.message,
              },
            },
          };
      }
    }

    return state;
  };
}

export const isRequiredToFetch = (
  state: PaginatedCollectionState,
  query: string,
  page: number,
): boolean => {
  return !pageSelector(state, query, page);
};

export function pageSelector(
  state: PaginatedCollectionState,
  query: string,
  page: number,
): CollectionState | null {
  const key = getKey(query);
  if (state[key] && state[key][page]) {
    return state[key][page];
  }
  return null;
}

export function totalSelector(
  state: PaginatedCollectionState,
  query: string,
): number {
  const key = getKey(query);
  if (state[key] && state[key]) {
    return state[key].total;
  }
  return 0;
}
